import axiosApiInstance from '../../../api/axiosClient';

export type SelectOption = { label: string; value: number };

const normalizeLabelKey = (name: string) =>
    name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');

/**
 * Believe Label options for release forms.
 *
 * Hides only mistaken YouTube-style Label rows when a proper spaced Label
 * already exists for the same name — e.g. drop "PrimeProductions" when both
 * Asset Label "PrimeProductions" and Label "Prime Productions" exist.
 *
 * Same-name pairs like Label "Bossy" + Asset Label "Bossy" stay visible:
 * Label and Asset Label are separate fields.
 */
export const fetchLabelOptions = async (): Promise<SelectOption[]> => {
    try {
        const [labelsRes, assetRes] = await Promise.all([
            axiosApiInstance.get<API.MusicDistribution.Label[]>(`/api/products/labels/`),
            axiosApiInstance.get<API.MusicDistribution.AssetLabel[]>(`/api/products/asset_labels/`)
        ]);

        const labels = labelsRes.data.filter((label) => (label.name || '').trim());

        const assetKeys = new Set(
            assetRes.data
                .map((a) => normalizeLabelKey(a.name || ''))
                .filter(Boolean)
        );

        const spacedLabelKeys = new Set(
            labels
                .filter((label) => /\s/.test((label.name || '').trim()))
                .map((label) => normalizeLabelKey(label.name || ''))
                .filter(Boolean)
        );

        return labels
            .filter((label) => {
                const name = (label.name || '').trim();
                const key = normalizeLabelKey(name);
                const isSpaceless = !/\s/.test(name);
                // Hide spaceless Label only when it duplicates an Asset Label AND
                // a spaced Believe-style Label for the same key already exists.
                if (isSpaceless && assetKeys.has(key) && spacedLabelKeys.has(key)) {
                    return false;
                }
                return true;
            })
            .map((label) => ({
                label: label.name,
                value: label.ID
            }));
    } catch (reason) {
        console.error(reason);
        return [];
    }
};

export const fetchAssetLabelOptions = async (): Promise<SelectOption[]> => {
    try {
        const { data } = await axiosApiInstance.get<API.MusicDistribution.AssetLabel[]>(
            `/api/products/asset_labels/`
        );
        return data
            .filter((label) => (label.name || '').trim())
            .map((label) => ({
                label: label.name,
                value: label.ID
            }));
    } catch (reason) {
        console.error(reason);
        return [];
    }
};
